import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Box, Divider, Grid, IconButton, MenuItem, Select, TablePagination } from '@mui/material';
import React, { useState, useContext, useEffect } from 'react';
import '../../../styles/cssStyles.css';
import { useTheme } from '@mui/material/styles';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import range from 'lodash/range';
import PropTypes from 'prop-types';
import { Typography } from '@uhc-tempo/components';
import { Paper, TextField } from '@uhc-tempo/components/node_modules/@mui/material';
import { FormContext } from '../../../context/Form.context.js';
import CarePathService from '../../../API/CarePathService.js';
import displayLoadingIndicator from '../../../common/components/LoadingIndicator.components.js';
import { getServiceNameToDisplay } from '../../../utils/EstimatePageUtil.js';

export default function RevCodesComponent(props) {
    const { otherLocationsOpen, onServiceSelect, onClose } = props;
    const classes = styles(theme);
    const { formValues, getIncrementedCorId } = useContext(FormContext);
    const { selectedService } = formValues;
    const [medicalServices, setMedicalServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState(medicalServices);
    const rowsPerPage = 4;
    const [page, setPage] = useState(0); //trackcurrentpage
    const [pageBeforeFilter, setPageBeforeFilter] = useState(0); //trackcurrentpagebeforefilter
    const [isFiltering, setIsFiltering] = useState(false); //trackfilteringstate
    const [searchValue, setSearchValue] = useState(''); //tracksearchvalue
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        if (!isFiltering) {
            setPageBeforeFilter(newPage); // we don't want to update pageBeforeFilter when filtering
        }
    };
    const [loading, SetLoading] = useState(true);
    const textRef = React.useRef(null);
    const startIndex = page * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, filteredServices.length);

    useEffect(() => {
        if (selectedService?.value?.fullCarePath?.id) {
            async function fetchData() {
                const response = await CarePathService.getCarepathDataForRevenueCode(
                    selectedService?.value?.fullCarePath?.id,
                    getIncrementedCorId()
                );
                const serviceFilterResponse =
                    response &&
                    response.filter(
                        (res) =>
                            res.carePathCategoryCode ===
                            selectedService?.value?.fullCarePath?.coverageTypeCode
                    );

                setMedicalServices(serviceFilterResponse);
                setFilteredServices(serviceFilterResponse);
                SetLoading(false);
            }
            fetchData();
        }
    }, [selectedService]);

    const handleFilterChange = (event) => {
        const { value } = event.target;
        setSearchValue(value);
        const filtered = medicalServices.filter((service) => {
            const serviceName = service.name.toLowerCase();
            const serviceDescription = service.description.toLowerCase();
            const filterValue = value.toLowerCase();
            return serviceName.includes(filterValue) || serviceDescription.includes(filterValue);
        });
        if (value.length !== 0) {
            setIsFiltering(true);
            setPage(0); // setting page to the first page when filter is applied
        } else {
            setIsFiltering(false);
            setPage(pageBeforeFilter); // setting page to the page before filter when filter is cleared
        }
        setFilteredServices(filtered);
    };

    const TablePaginationActions = (props) => {
        const theme = useTheme();
        const { count, page, rowsPerPage, onPageChange } = props;

        const handleBackButtonClick = (event) => {
            onPageChange(event, page - 1);
        };

        const handleNextButtonClick = (event) => {
            onPageChange(event, page + 1);
        };

        const handlePageDropDown = (event) => {
            // we're grabbing from human readable so need to offset by 1
            setPage(event.target.value - 1);
        };

        return (
            <Grid container direction="row" wrap="nowrap">
                <Grid item style={{ paddingTop: '16px' }}>
                    <IconButton
                        onClick={(e) => {
                            e.preventDefault();
                            handleBackButtonClick();
                        }}
                        disabled={page <= 0}
                        aria-label="previous page"
                        style={{ fill: '#333333', paddingLeft: '0px' }}
                        sx={{
                            '&:focus': {
                                outline: '#196ecf auto 4px',
                                borderRadius: '0px',
                            },
                        }}
                    >
                        {theme.direction === 'rtl' ? <ArrowForward /> : <ArrowBack />}
                        <Typography.Footnote
                            style={{
                                marginBottom: '0px',
                                ...classes.boldFontWeight,
                                fontSize: '14.22px',
                                color: page <= 0 ? '#0000008a' : '#000000de',
                                paddingLeft: '8px',
                            }}
                        >
                            Previous
                        </Typography.Footnote>
                    </IconButton>
                </Grid>
                <Grid
                    item
                    style={{
                        paddingTop: '13px',
                    }}
                >
                    <Select
                        aria-busy={otherLocationsOpen} //suppress the announcement for dialog opening saad
                        onChange={handlePageDropDown}
                        value={medicalServices && medicalServices?.length ? page + 1 : ''}
                        style={{
                            ...classes.boldFontWeight,
                            fontSize: '14.22px',
                            paddingLeft: '24px',
                            paddingRight: '16px',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        }}
                        sx={{
                            '&:focus': {
                                outline: '#196ecf auto 1px !important',
                            },
                            '&&&:before': {
                                borderBottom: 'none',
                            },
                            '&&:after': {
                                borderBottom: 'none',
                            },
                        }}
                        variant="standard"
                    >
                        {pageSelectionOptions.map((pageOption) => (
                            <MenuItem
                                id="intraPageMenuItem"
                                value={medicalServices && medicalServices?.length && pageOption}
                                key={pageOption}
                                sx={{
                                    '&:focus': {
                                        outline: '#196ecf auto 1px !important',
                                    },
                                }}
                                aria-current={page + 1 == pageOption ? 'true' : 'false'}
                            >
                                Page {pageOption} of {pageSelectionOptions.length}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item style={{ paddingTop: '16px' }}>
                    <IconButton
                        onClick={(e) => {
                            e.preventDefault();
                            handleNextButtonClick();
                        }}
                        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                        aria-label="next page"
                        data-testid="next page"
                        style={{ fill: '#333333' }}
                        sx={{
                            '&:focus': {
                                outline: '#196ecf auto 1px',
                                borderRadius: '0px',
                            },
                        }}
                    >
                        <Typography.Footnote
                            style={{
                                marginBottom: '0px',
                                ...classes.boldFontWeight,
                                fontSize: '14.22px',
                                color:
                                    page >= Math.ceil(count / rowsPerPage) - 1
                                        ? '#0000008a'
                                        : '#000000de',
                                paddingRight: '8px',
                            }}
                        >
                            Next
                        </Typography.Footnote>
                        {theme.direction === 'rtl' ? <ArrowBack /> : <ArrowForward />}
                    </IconButton>
                </Grid>
            </Grid>
        );
    };

    TablePaginationActions.propTypes = {
        count: PropTypes.number.isRequired,
        onPageChange: PropTypes.func.isRequired,
        page: PropTypes.number.isRequired,
        rowsPerPage: PropTypes.number.isRequired,
    };

    // creates an array containing 1-n numbered pages for display in dropdown selection menu
    // if number of estimates is not greater than 0, returns [1]
    const pageSelectionOptions =
        filteredServices?.length > 0
            ? range(1, [Math.ceil(filteredServices.length / rowsPerPage) + 1])
            : [1];
    const renderServiceInformation = () => {
        const serviceNameToDisplay = getServiceNameToDisplay(selectedService?.value);
        const styleTypography = {
            marginBottom: 0,
            paddingRight: '4px',
            ...classes.boldFontWeight,
            color: '#333333',
        };
        return (
            <Typography.H4
                style={{
                    ...styleTypography,
                }}
            >
                {serviceNameToDisplay}
            </Typography.H4>
        );
    };

    return (
        <Box sx={{ width: '100%' }} data-testid="revenuePage">
            {loading ? (
                displayLoadingIndicator(false)
            ) : (
                <>
                    <Grid container direction="column">
                        <Grid item xs={12}>
                            <Grid
                                container
                                direction="row"
                                style={{ paddingBottom: '24px', paddingTop: '32px' }}
                            >
                                <Grid item xs={12}>
                                    <Grid container justifyContent="space-between">
                                        <div
                                            aria-live="polite" //announce filter / initial search results
                                            aria-atomic="true" //assert to capture child element
                                            aria-owns="cptCode" //set accessibility tree ownership
                                            aria-labelledby="resultsFor cptCode" //concatenate element contents for alert
                                        >
                                            <Typography.H6
                                                id="resultsFor"
                                                data-testid="resultsFor"
                                                style={{
                                                    marginBottom: 0,
                                                    paddingBottom: '4px',
                                                    ...classes.boldFontWeight,
                                                    color: '#333333',
                                                }}
                                            >
                                                {medicalServices && medicalServices?.length
                                                    ? medicalServices.length
                                                    : 0}{' '}
                                                results for
                                            </Typography.H6>
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid id="cptCode" item xs={10}>
                                    <Grid container direction="row">
                                        <Grid item xs={12}>
                                            {renderServiceInformation()}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} minWidth={'120px'}>
                        {medicalServices?.length > rowsPerPage && (
                            <Paper
                                style={{
                                    borderRadius: '32px',
                                    padding: '5px',
                                    paddingLeft: '21px',
                                    'max-width': '360px',
                                    height: '50px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    textRef.current.focus();
                                }}
                                variant="outlined"
                            >
                                <TextField
                                    placeholder="Type a detailed description"
                                    variant="outlined"
                                    focused={true}
                                    inputRef={textRef}
                                    sx={{
                                        width: '100%',
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#6F6F6F',
                                            size: '14px',
                                            ...classes.boldFontWeight,
                                            cursor: 'pointer',
                                            marginTop: '7px',
                                        },
                                        '.MuiInputBase-input ': {
                                            cursor: 'pointer',
                                        },
                                    }}
                                    label="Filter your results"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    onChange={handleFilterChange}
                                />
                            </Paper>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={classes.divider} data-testid="headerDivider"></Divider>
                    </Grid>
                    {filteredServices.slice(startIndex, endIndex).map((service, index) => (
                        <React.Fragment key={startIndex + index}>
                            <Grid
                                item
                                xs={12}
                                onClick={async () => {
                                    onServiceSelect(service);
                                    onClose();
                                }}
                            >
                                <Typography.H4
                                    key={index}
                                    align="left"
                                    style={{
                                        color: '#196ECF',
                                        ...classes.boldFontWeight,
                                        marginBottom: '0px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {service.name}
                                </Typography.H4>
                                <Typography.Paragraph
                                    align="left"
                                    style={{
                                        ...classes.boldFontWeight,
                                        color: '#333333',
                                    }}
                                >
                                    {service.description}
                                </Typography.Paragraph>
                            </Grid>
                            {index !== medicalServices?.length - 1 && (
                                <Grid item xs={12}>
                                    <Divider sx={classes.divider}></Divider>
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                    {filteredServices.length > rowsPerPage && (
                        <TablePagination
                            data-testid="tablePagination"
                            component="div"
                            count={
                                filteredServices.length
                                    ? filteredServices.length == 1
                                        ? 1
                                        : filteredServices.length
                                    : 0
                            }
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[]}
                            labelRowsPerPage=""
                            onPageChange={handlePageChange}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={() => {
                                '';
                            }}
                            role="navigation"
                        />
                    )}

                    {/* display no rev code messaging */}
                    {medicalServices?.length === 0 && (
                        <Box data-testid="noRevCodeResBanner">
                            <Grid>
                                <Typography.H4
                                    style={{
                                        width: '789px',
                                        height: '32px',
                                        top: '501px',
                                        left: '85px',
                                        gap: '0px',
                                        opacity: '0px',
                                        color: '#002677',
                                        fontSize: '25.63px',
                                        ...classes.boldFontWeight,
                                        lineHeight: '32px',
                                        textAlign: 'left',
                                    }}
                                >
                                    Unable to display an estimate for{' '}
                                    {getServiceNameToDisplay(selectedService?.value)}
                                </Typography.H4>
                                <br />
                                <Typography.Paragraph
                                    style={{
                                        width: '802px',
                                        height: '136px',
                                        top: '581px',
                                        left: '86px',
                                        gap: '0px',
                                        opacity: '0px',
                                        styleName: 'UHC/P/MD | Normal',
                                        fontSize: '16px',
                                        fontWeight: '400',
                                        lineHeight: '20px',
                                        textAlign: 'left',
                                        color: '#212223',
                                    }}
                                >
                                    The item you searched for may no longer be available or may be
                                    part of a more comprehensive procedure. To provide you with the
                                    best estimate, try again using these suggestions:
                                    <ul>
                                        <li>
                                            {' '}
                                            Search for the covered item or service to be performed
                                            (Example: MRI leg or knee replacement){' '}
                                        </li>
                                        <li>
                                            {' '}
                                            Get the billing code for item or service to be performed
                                            (Example: CPT 99213 or HCPC K1093)
                                        </li>
                                        <li> Make sure you’re typing in the correct code</li>
                                    </ul>
                                </Typography.Paragraph>
                            </Grid>
                        </Box>
                    )}
                    {medicalServices?.length > 0 && filteredServices.length === 0 && (
                        <Box data-testid="noFilterResBanner">
                            <Grid>
                                <Typography.Paragraph
                                    style={{
                                        width: '682px',
                                        height: '120px',
                                        top: '501px',
                                        left: '85.5px',
                                        gap: '0px',
                                        opacity: '0px',
                                        fontSize: '16px',
                                        fontWeight: '400',
                                        lineHeight: '20px',
                                        textAlign: 'left',
                                        color: '#212223',
                                        styleName: 'UHC/P/MD | Normal',
                                        fontStyle: 'normal',
                                        marginTop: '35px',
                                    }}
                                >
                                    No results for{' '}
                                    <b style={classes.boldFontWeight}>{searchValue}</b> within{' '}
                                    <b>{getServiceNameToDisplay(selectedService?.value)}</b>
                                    <span>
                                        <br /> <br />
                                        <b>Suggestions:</b>
                                        <ul style={{ margin: 0 }}>
                                            <li>
                                                Filter for the covered item or service to be
                                                performed (Example: MRI leg or knee replacement)
                                            </li>
                                            <li>
                                                Get the billing code for item or service to be
                                                performed (Example: CPT 99213 or HCPC K1093)
                                            </li>
                                            <li>
                                                Return to the search bar and try another keyword or
                                                billing code
                                            </li>
                                        </ul>
                                    </span>
                                </Typography.Paragraph>
                            </Grid>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
